import React, { useState } from 'react';
import { Button, Space, message, Form as AntForm, Tooltip, InputNumber, Card, Row, Col, Typography, Select, Alert } from 'antd';
import { EditOutlined, ClearOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm, Controller } from 'react-hook-form';

import useAuthStore from '../../store/useAuthStore';
import usePermissions from '../../hooks/usePermissions';
import { MODULE_IDS } from '../../utils/constants';
import { formatCurrency, formatQty } from '../../utils/formatters';

import { getMappings, createMapping, updateMapping, deleteMapping } from '../../api/mappings.api';
import { getUnits } from '../../api/units.api';

import PageHeader from '../../components/common/PageHeader';
import DataTable from '../../components/common/DataTable';
import FormDrawer from '../../components/common/FormDrawer';
import ConfirmDelete from '../../components/common/ConfirmDelete';
import SearchableSelect from '../../components/common/SearchableSelect';

const { Text } = Typography;

const MappingPage = () => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editingMapping, setEditingMapping] = useState(null);
  
  const [filterReadyProductId, setFilterReadyProductId] = useState(null);
  const [filterRawMaterialId, setFilterRawMaterialId] = useState(null);
  const [tableParams, setTableParams] = useState({ current: 1, pageSize: 50 });

  const { user } = useAuthStore();
  const plantId = user?.plantID;
  const queryClient = useQueryClient();

  const { canAdd, canEdit, canDelete } = usePermissions(MODULE_IDS.MAPPING);

  const { control, handleSubmit, reset, watch, formState: { errors, isDirty }, setValue } = useForm({
    defaultValues: {
      readyProductID: null,
      rawMaterialID: null,
      productionQty: 1,
      consumptionQty: 1,
      wastagePercent: 0,
      unitID: null,
      rate: 0
    }
  });


  const wProdQty = watch('productionQty');
  const wConsQty = watch('consumptionQty');
  const wUnitID = watch('unitID');
  

  const [selectedProductName, setSelectedProductName] = useState('');
  const [selectedRawName, setSelectedRawName] = useState('');

  const { data, isLoading: isFetching } = useQuery({
    queryKey: ['mappings', plantId, filterReadyProductId, filterRawMaterialId, tableParams.current, tableParams.pageSize],
    queryFn: () => getMappings(plantId, { 
      readyProductId: filterReadyProductId,
      rawMaterialId: filterRawMaterialId,
      page: tableParams.current, 
      pageSize: tableParams.pageSize 
    }),
    enabled: !!plantId,
  });

  const mappings = data?.items || [];
  const totalCount = data?.total || 0;


  const { data: unitsData = [] } = useQuery({
    queryKey: ['units', plantId],
    queryFn: () => getUnits(plantId),
    enabled: !!plantId,
  });

  const createMutation = useMutation({
    mutationFn: (data) => createMapping(plantId, data),
    onSuccess: () => {
      message.success('Mapping created successfully!');
      queryClient.invalidateQueries({ queryKey: ['mappings'] });
      closeDrawer();
    },
    onError: (err) => message.error(err.message || 'Error creating mapping')
  });

  const updateMutation = useMutation({
    mutationFn: (data) => updateMapping(plantId, editingMapping.mappingID, data),
    onSuccess: () => {
      message.success('Mapping updated successfully!');
      queryClient.invalidateQueries({ queryKey: ['mappings'] });
      closeDrawer();
    },
    onError: (err) => message.error(err.message || 'Error updating mapping')
  });

  const deleteMutation = useMutation({
    mutationFn: (mappingId) => deleteMapping(plantId, mappingId),
    onSuccess: () => {
      message.success('Mapping deleted successfully!');
      queryClient.invalidateQueries({ queryKey: ['mappings'] });
    },
    onError: (err) => message.error(err.message || 'Cannot delete mapping.')
  });

  const openDrawer = (record = null) => {
    setEditingMapping(record);
    if (record) {
      reset({
        readyProductID: record.readyProductID,
        rawMaterialID: record.rawMaterialID,
        productionQty: record.productionQty,
        consumptionQty: record.consumptionQty,
        wastagePercent: record.wastagePercent,
        unitID: record.unitID,
        rate: record.rate || 0
      });
      setSelectedProductName(record.productName);
      setSelectedRawName(record.rawName);
    } else {
      reset({
        readyProductID: null, rawMaterialID: null,
        productionQty: 1, consumptionQty: 1, wastagePercent: 0, unitID: null, rate: 0
      });
      setSelectedProductName('');
      setSelectedRawName('');
    }
    setDrawerOpen(true);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setEditingMapping(null);
  };

  const onSubmit = (formData) => {
    if (editingMapping) {
      updateMutation.mutate(formData);
    } else {
      createMutation.mutate(formData);
    }
  };

  const handleClearFilters = () => {
    setFilterReadyProductId(null);
    setFilterRawMaterialId(null);
    setTableParams({ current: 1, pageSize: 50 });
  };

  
  const getPreviewText = () => {
    if (!wProdQty || !wConsQty) return null;
    const calc = (wConsQty / wProdQty) * 100;
    const selectedUnitObj = unitsData.find(u => u.unitID === wUnitID);
    const unitName = selectedUnitObj ? selectedUnitObj.consumptionUnit : 'units';
    const prodName = selectedProductName || 'Product';
    const rawName = selectedRawName || 'Raw Material';
    
    return `For 100 units of ${prodName}, you need ${calc.toFixed(4)} ${unitName} of ${rawName}`;
  };


  const columns = [
    { title: '#', dataIndex: 'index', render: (text, record, index) => (tableParams.current - 1) * tableParams.pageSize + index + 1, width: 60 },
    { title: 'Ready Product', dataIndex: 'productName', key: 'productName' },
    { title: 'Raw Material', dataIndex: 'rawName', key: 'rawName' },
    { title: 'Prod Qty', dataIndex: 'productionQty', key: 'productionQty', render: (val) => formatQty(val) },
    { title: 'Consumption Qty', dataIndex: 'consumptionQty', key: 'consumptionQty', render: (val) => formatQty(val) },
    { title: 'Wastage %', dataIndex: 'wastagePercent', key: 'wastagePercent', render: (val) => `${val}%` },
    { title: 'PUnit/CUnit', dataIndex: 'unitDisplayName', key: 'unitDisplayName' },
    { title: 'Rate (₹)', dataIndex: 'rate', key: 'rate', render: (val) => formatCurrency(val) },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      render: (_, record) => (
        <Space size="middle">
          {canEdit && (
            <Tooltip title="Edit">
              <Button type="text" icon={<EditOutlined style={{ color: '#1890ff' }} />} onClick={() => openDrawer(record)} />
            </Tooltip>
          )}
          {canDelete && (
            <ConfirmDelete 
              itemName="Mapping" 
              onConfirm={() => deleteMutation.mutate(record.mappingID)}
              loading={deleteMutation.isPending && deleteMutation.variables === record.mappingID}
            />
          )}
        </Space>
      )
    }
  ];

  return (
    <div>
      <PageHeader 
        title="Ready vs Raw Material Mapping" 
        subtitle="Define recipes/BOM (Bill of Materials) for your products"
        onAdd={() => openDrawer(null)} 
        canAdd={canAdd} 
        addLabel="Add Mapping"
      />

      <Card style={{ marginBottom: 20 }} bodyStyle={{ padding: '16px' }}>
        <Row gutter={[16, 16]} align="middle">
          <Col xs={24} sm={10} md={8}>
            <SearchableSelect 
              apiUrl={`/plants/${plantId}/ready-products`}
              valueField="rpid"
              labelField="productName"
              placeholder="Filter by Ready Product"
              value={filterReadyProductId}
              onSelect={(val) => {
                setFilterReadyProductId(val ? val.rpid : null);
                setTableParams(prev => ({ ...prev, current: 1 }));
              }}
            />
          </Col>
          <Col xs={24} sm={10} md={8}>
            <SearchableSelect 
              apiUrl={`/plants/${plantId}/raw-materials`}
              valueField="rawID"
              labelField="rawName"
              placeholder="Filter by Raw Material"
              value={filterRawMaterialId}
              onSelect={(val) => {
                setFilterRawMaterialId(val ? val.rawID : null);
                setTableParams(prev => ({ ...prev, current: 1 }));
              }}
            />
          </Col>
          <Col xs={24} sm={4} md={8}>
            <Button icon={<ClearOutlined />} onClick={handleClearFilters}>
              Clear
            </Button>
          </Col>
        </Row>
      </Card>

      <DataTable 
        columns={columns} 
        data={mappings} 
        loading={isFetching} 
        rowKey="mappingID"
        pagination={{
          current: tableParams.current,
          pageSize: tableParams.pageSize,
          total: totalCount
        }}
        onChange={(pagination) => setTableParams({ current: pagination.current, pageSize: pagination.pageSize })}
      />

      <FormDrawer
        title={editingMapping ? "Edit Mapping" : "Add Mapping"}
        open={drawerOpen}
        onClose={closeDrawer}
        onSave={handleSubmit(onSubmit)}
        loading={createMutation.isPending || updateMutation.isPending}
        isDirty={isDirty}
        width={500}
      >
        <AntForm layout="vertical">
          <AntForm.Item label="Ready Product" validateStatus={errors.readyProductID ? 'error' : ''} help={errors.readyProductID?.message} required>
            <Controller
              name="readyProductID"
              control={control}
              rules={{ required: 'Ready Product is required' }}
              render={({ field }) => (
                <SearchableSelect 
                  {...field}
                  apiUrl={`/plants/${plantId}/ready-products`}
                  valueField="rpid"
                  labelField="productName"
                  placeholder="Select Ready Product"
                  disabled={!!editingMapping}
                  onSelect={(obj) => {
                    field.onChange(obj ? obj.rpid : null);
                    setSelectedProductName(obj ? obj.productName : '');
                  }}
                />
              )}
            />
          </AntForm.Item>

          <AntForm.Item label="Raw Material" validateStatus={errors.rawMaterialID ? 'error' : ''} help={errors.rawMaterialID?.message} required>
            <Controller
              name="rawMaterialID"
              control={control}
              rules={{ required: 'Raw Material is required' }}
              render={({ field }) => (
                <SearchableSelect 
                  {...field}
                  apiUrl={`/plants/${plantId}/raw-materials`}
                  valueField="rawID"
                  labelField="rawName"
                  placeholder="Select Raw Material"
                  disabled={!!editingMapping} 
                  onSelect={(obj) => {
                    field.onChange(obj ? obj.rawID : null);
                    setSelectedRawName(obj ? obj.rawName : '');
    
                    if (obj && obj.unitID && !editingMapping) {
                       setValue('unitID', obj.unitID);
                    }
                  }}
                />
              )}
            />
          </AntForm.Item>

          <Row gutter={16}>
            <Col span={12}>
              <AntForm.Item label="Production Qty(in Pic)" validateStatus={errors.productionQty ? 'error' : ''} help={errors.productionQty?.message || "How many units this recipe produces"} required>
                <Controller
                  name="productionQty"
                  control={control}
                  rules={{ required: true, min: 0.0001 }}
                  render={({ field }) => <InputNumber {...field} style={{ width: '100%' }} min={0.0001} />}
                />
              </AntForm.Item>
            </Col>
            <Col span={12}>
              <AntForm.Item label="Consumption Qty" validateStatus={errors.consumptionQty ? 'error' : ''} help={errors.consumptionQty?.message || "Raw material consumed"} required>
                <Controller
                  name="consumptionQty"
                  control={control}
                  rules={{ required: true, min: 0.0001 }}
                  render={({ field }) => <InputNumber {...field} style={{ width: '100%' }} min={0.0001} />}
                />
              </AntForm.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col span={12}>
              <AntForm.Item label="Wastage %" validateStatus={errors.wastagePercent ? 'error' : ''} help={errors.wastagePercent?.message}>
                <Controller
                  name="wastagePercent"
                  control={control}
                  rules={{ min: 0, max: 100 }}
                  render={({ field }) => <InputNumber {...field} style={{ width: '100%' }} min={0} max={100} addonAfter="%" />}
                />
              </AntForm.Item>
            </Col>
            <Col span={12}>
              <AntForm.Item label="Unit" validateStatus={errors.unitID ? 'error' : ''} help={errors.unitID?.message} required>
                <Controller
                  name="unitID"
                  control={control}
                  rules={{ required: 'Unit is required' }}
                  render={({ field }) => (
                    <Select {...field} placeholder="Select Unit" style={{ width: '100%' }} options={unitsData.map(u => ({
                      value: u.unitID,
                      label: ` ${u.consumptionUnit}`
                    }))} />
                  )}
                />
              </AntForm.Item>
            </Col>
          </Row>

          <AntForm.Item label="Rate (₹) per Unit" validateStatus={errors.rate ? 'error' : ''} help={errors.rate?.message}>
            <Controller
              name="rate"
              control={control}
              rules={{ min: 0 }}
              render={({ field }) => <InputNumber {...field} style={{ width: '100%' }} min={0} />}
            />
          </AntForm.Item>

          {wProdQty > 0 && wConsQty > 0 && selectedProductName && selectedRawName && (
            <Alert 
              title="Recipe Preview" 
              description={getPreviewText()} 
              type="info" 
              showIcon 
              style={{ marginTop: 16 }}
            />
          )}

        </AntForm>
      </FormDrawer>
    </div>
  );
};

export default MappingPage;